import Phaser from "phaser";
import {
  spawnSkillFx,
  spawnBasicAttackFx,
  spawnDamageNumber,
} from "@/entities/SkillEffects";
import { Player } from "@/entities/Player";
import { Monster } from "@/entities/Monster";
import { ZenPile } from "@/entities/ZenPile";
import { EquipPile } from "@/entities/EquipPile";
import itemsData from "@/data/items.json";
import { useCharacterStore } from "@/state/characterStore";
import { useUIStore } from "@/state/uiStore";
import { useFieldStore } from "@/state/fieldStore";
import { ingestClockSnapshot } from "@/state/clockStore";
import type {
  FieldSnapshot,
  AttackResp,
  MonsterHitMsg,
  PlayerAttackMsg,
  ZenDropPush,
  ZenPickupPush,
  EquipDropPush,
  EquipPickupPush,
  EliteSpawnPush,
  EliteDropPush,
} from "@/network/messages";
import type { FieldScene } from "../FieldScene";
import { isPhaserPointerOverBlockingUI } from "@/ui/pointerBlock";

interface EquipTpl {
  id: number;
  name: string;
  type: string;
  slot?: string;
}
const EQUIP_TPL: Record<number, EquipTpl> = Object.fromEntries(
  (itemsData.items as EquipTpl[]).map((i) => [i.id, i]),
);

function equipColor(tpl: EquipTpl | undefined): number {
  if (!tpl) return 0xffffff;
  switch (tpl.type) {
    case "weapon":
      return 0xe67e22;
    case "shield":
      return 0x7f8c8d;
    case "armor":
      return 0x95a5a6;
    default:
      return 0x9b59b6;
  }
}

function spawnEquipPile(scene: FieldScene, msg: EquipDropPush): void {
  if (scene.equipPiles.has(msg.id)) return;
  const tpl = EQUIP_TPL[msg.templateId];
  const pile = new EquipPile({
    scene,
    id: msg.id,
    x: msg.x,
    y: msg.y,
    templateId: msg.templateId,
    upgrade: msg.upgrade,
    label: tpl?.name ?? `#${msg.templateId}`,
    iconColor: equipColor(tpl),
    ownerUid: msg.ownerUid,
    ownerUntil: msg.ownerUntil,
  });
  pile.on("pointerup", (pointer: Phaser.Input.Pointer) => {
    if (isPhaserPointerOverBlockingUI(pointer)) return;
    scene.automation.clickEquipPile(msg.id);
  });
  scene.equipPiles.set(msg.id, pile);
}

export function onState(scene: FieldScene, snap: FieldSnapshot) {
  if (!snap) return;
  if (snap.clock) ingestClockSnapshot(snap.clock);
  // Players
  const seen = new Set<string>();
  for (const ps of snap.players ?? []) {
    seen.add(ps.uid);
    if (ps.uid === scene.localUid) {
      // Local player: cập nhật HP/MP thực tế cho HUD vitals.
      // Death/respawn are handled exclusively via the server pushes
      // (player_death / player_respawn). Do NOT trigger death from snapshot
      // HP — the server is authoritative on the death timeline.
      const st = useCharacterStore.getState();
      const patch: {
        hp?: number;
        mp?: number;
        maxHp?: number;
        maxMp?: number;
      } = {};
      if (typeof ps.hp === "number" && ps.hp >= 0) {
        patch.hp = ps.hp;
        if (typeof ps.maxHp === "number" && ps.maxHp > 0)
          patch.maxHp = ps.maxHp;
        else if (ps.hp > st.maxHp) patch.maxHp = ps.hp;
      }
      if (typeof ps.mp === "number" && ps.mp >= 0) {
        patch.mp = ps.mp;
        if (typeof ps.maxMp === "number" && ps.maxMp > 0)
          patch.maxMp = ps.maxMp;
        else if (ps.mp > st.maxMp) patch.maxMp = ps.mp;
      }
      if (Object.keys(patch).length) st.setVitals(patch);
      continue;
    }
    let pl = scene.players.get(ps.uid);
    if (!pl) {
      pl = new Player({
        scene,
        x: ps.x,
        y: ps.y,
        uid: ps.uid,
        name: ps.name,
        klass: (ps.klass as "warrior" | "wizard" | "archer") ?? "warrior",
      });
      scene.players.set(ps.uid, pl);
    }
    pl.setRemoteTarget(ps.x, ps.y);
    pl.setHp(ps.hp, Math.max(ps.hp, pl.maxHp));
  }
  for (const uid of [...scene.players.keys()]) {
    if (uid !== scene.localUid && !seen.has(uid)) {
      scene.players.get(uid)?.destroy();
      scene.players.delete(uid);
    }
  }
  // Monsters
  const mseen = new Set<number>();
  for (const m of snap.monsters ?? []) {
    mseen.add(m.id);
    // Ignore dead monsters still present in snapshot — the client has already
    // played the death fade-out and removed the sprite. They'll be cleared
    // from `deadMonsters` once the server stops broadcasting them OR once
    // the server respawns them in-place (same id, hp restored).
    if (scene.deadMonsters.has(m.id)) {
      // Server reuses the monster id on respawn (see field_ai.go respawnDead).
      // When that happens the snapshot will report full HP again — drop the
      // dead-id memory so we re-spawn the sprite below.
      if (m.hp > 0 && m.hp >= m.maxHp) {
        scene.deadMonsters.delete(m.id);
      } else {
        continue;
      }
    }
    if (m.hp <= 0 && !scene.monsters.has(m.id)) {
      // Never spawn a fresh corpse from a stale snapshot.
      scene.deadMonsters.add(m.id);
      continue;
    }
    let mob = scene.monsters.get(m.id);
    if (!mob) {
      mob = new Monster({
        scene,
        id: m.id,
        tplId: m.tplId,
        x: m.x,
        y: m.y,
        hp: m.hp,
        maxHp: m.maxHp,
        isElite: !!m.isElite,
        scale: m.scale,
      });
      mob.on("pointerup", (pointer: Phaser.Input.Pointer) => {
        if (isPhaserPointerOverBlockingUI(pointer)) return;
        if (scene.targetMonster && scene.targetMonster !== mob)
          scene.targetMonster.setSelected(false);
        scene.targetMonster = mob;
        mob!.setSelected(true);
        useUIStore.getState().showToast(`Target: ${mob!.id}`);
      });
      mob.once(Phaser.GameObjects.Events.DESTROY, () => {
        if (scene.targetMonster === mob) scene.targetMonster = undefined;
        if (scene.monsters.get(m.id) === mob) scene.monsters.delete(m.id);
      });
      scene.monsters.set(m.id, mob);
    } else if (!!m.isElite !== mob.isElite) {
      mob.setElite(!!m.isElite, m.scale ?? 1.3);
    }
    mob.setTarget(m.x, m.y);
    mob.setHp(m.hp);
    if (m.hp <= 0) scene.deadMonsters.add(m.id);
  }
  for (const id of [...scene.monsters.keys()]) {
    if (!mseen.has(id)) {
      const m = scene.monsters.get(id);
      if (scene.targetMonster === m) scene.targetMonster = undefined;
      m?.destroy();
      scene.monsters.delete(id);
    }
  }
  // Clear dead-id memory once the server stops broadcasting that monster,
  // so respawned monsters with the same id can spawn fresh.
  for (const id of [...scene.deadMonsters]) {
    if (!mseen.has(id)) scene.deadMonsters.delete(id);
  }
  // Throttle minimap store updates — full array rebuild + React re-render
  // every snapshot was a major source of FPS drops on monster spawn bursts.
  const fScene = scene as FieldScene & { _miniMapNextMs?: number };
  const nowMs = (typeof performance !== "undefined" ? performance.now() : Date.now());
  if (!fScene._miniMapNextMs || nowMs >= fScene._miniMapNextMs) {
    fScene._miniMapNextMs = nowMs + 500;
    useFieldStore
      .getState()
      .setMonsters(
        [...scene.monsters.values()].map((m) => ({
          id: m.id,
          tplId: m.tplId,
          x: m.x,
          y: m.y,
        })),
      );
  }
  // Zen piles reconcile (in case we missed a push or just joined)
  if (snap.piles) {
    const pseen = new Set<number>();
    for (const pl of snap.piles) {
      pseen.add(pl.id);
      if (!scene.zenPiles.has(pl.id)) {
        const pile = new ZenPile({
          scene,
          id: pl.id,
          x: pl.x,
          y: pl.y,
          amount: pl.amount,
          ownerUid: pl.ownerUid,
          ownerUntil: pl.ownerUntil,
        });
        pile.on("pointerup", (pointer: Phaser.Input.Pointer) => {
          if (isPhaserPointerOverBlockingUI(pointer)) return;
          scene.automation.clickPile(pl.id);
        });
        scene.zenPiles.set(pl.id, pile);
      }
    }
    for (const id of [...scene.zenPiles.keys()]) {
      if (!pseen.has(id)) {
        scene.zenPiles.get(id)?.fadeOutAndDestroy();
        scene.zenPiles.delete(id);
        if (scene.pickupWalkTargetId === id)
          scene.pickupWalkTargetId = undefined;
      }
    }
  }
  // Equip drops reconcile (snapshot path so late-joiners see existing piles).
  if (snap.equipDrops) {
    const eseen = new Set<number>();
    for (const ed of snap.equipDrops) {
      eseen.add(ed.id);
      spawnEquipPile(scene, ed);
    }
    for (const id of [...scene.equipPiles.keys()]) {
      if (!eseen.has(id)) {
        scene.equipPiles.get(id)?.fadeOutAndDestroy();
        scene.equipPiles.delete(id);
        if (scene.equipPickupWalkTargetId === id)
          scene.equipPickupWalkTargetId = undefined;
      }
    }
  }
}

export function onMonsterHit(scene: FieldScene, msg: MonsterHitMsg) {
  if (!scene.localPlayer) return;
  if (msg.targetUid && msg.targetUid !== scene.localUid) return;
  const attacker = scene.monsters.get(msg.monId);
  attacker?.flashAttack();
  spawnDamageNumber(
    scene,
    scene.localPlayer.x,
    scene.localPlayer.y,
    msg.damage,
    { crit: false },
  );
  useCharacterStore.getState().setVitals({ hp: msg.hp, maxHp: msg.maxHp });
  scene.localPlayer.setHp(msg.hp, msg.maxHp);
  // Death is announced via the player_death push (server-authoritative).
}

// Render attacks performed by other players so HP changes feel connected to a caster.
export function onPlayerAttack(scene: FieldScene, msg: PlayerAttackMsg) {
  if (msg.uid === scene.localUid) return; // local already drew its own FX
  const tgt = scene.monsters.get(msg.targetId);
  const from = { x: msg.fromX, y: msg.fromY };
  const to = tgt ? { x: tgt.x, y: tgt.y } : { x: msg.toX, y: msg.toY };
  const klass = (
    msg.klass === "wizard" || msg.klass === "archer" ? msg.klass : "warrior"
  ) as "warrior" | "wizard" | "archer";
  if (msg.skillId === 0) {
    spawnBasicAttackFx(scene, klass, from, to, () => {
      if (tgt && msg.damage > 0) {
        if (tgt.alive) tgt.flashHit({ fromX: from.x, fromY: from.y });
        spawnDamageNumber(scene, tgt.x, tgt.y, msg.damage, { crit: msg.crit });
      }
    });
  } else {
    const isHeal = msg.skillId === 3020;
    const fxTarget = isHeal ? from : to;
    spawnSkillFx(scene, msg.skillId, {
      caster: from,
      target: fxTarget,
      onImpact: () => {
        if (isHeal) {
          if (msg.damage > 0) {
            spawnDamageNumber(scene, from.x, from.y, msg.damage, {
              crit: msg.crit,
              heal: true,
            });
          }
        } else if (tgt && msg.damage > 0) {
          if (tgt.alive) tgt.flashHit({ fromX: from.x, fromY: from.y });
          spawnDamageNumber(scene, tgt.x, tgt.y, msg.damage, {
            crit: msg.crit,
          });
        }
      },
    });
  }
}

export function onZenDrop(scene: FieldScene, msg: ZenDropPush) {
  if (scene.zenPiles.has(msg.id)) return;
  const pile = new ZenPile({
    scene,
    id: msg.id,
    x: msg.x,
    y: msg.y,
    amount: msg.amount,
    ownerUid: msg.ownerUid,
    ownerUntil: msg.ownerUntil,
  });
  pile.on("pointerup", (pointer: Phaser.Input.Pointer) => {
    if (isPhaserPointerOverBlockingUI(pointer)) return;
    scene.automation.clickPile(msg.id);
  });
  scene.zenPiles.set(msg.id, pile);
}

export function onZenPickup(scene: FieldScene, msg: ZenPickupPush) {
  const p = scene.zenPiles.get(msg.id);
  if (!p) return;
  p.fadeOutAndDestroy();
  scene.zenPiles.delete(msg.id);
  if (scene.pickupWalkTargetId === msg.id)
    scene.pickupWalkTargetId = undefined;
}

export function onEquipDrop(scene: FieldScene, msg: EquipDropPush) {
  spawnEquipPile(scene, msg);
}

export function onEquipPickup(scene: FieldScene, msg: EquipPickupPush) {
  const p = scene.equipPiles.get(msg.id);
  if (!p) return;
  p.fadeOutAndDestroy();
  scene.equipPiles.delete(msg.id);
  if (scene.equipPickupWalkTargetId === msg.id)
    scene.equipPickupWalkTargetId = undefined;
}

// Elite Monster spawn alert (PRD_EliteMonster §5.4).
export function onEliteSpawn(scene: FieldScene, msg: EliteSpawnPush) {
  const mob = scene.monsters.get(msg.monId);
  if (mob && !mob.isElite) mob.setElite(true, 1.3);
  if (!scene.localPlayer) {
    useUIStore
      .getState()
      .showToast("⚡ Một Tinh Anh xuất hiện gần đây!", "warn");
    return;
  }
  const dx = msg.x - scene.localPlayer.x;
  const dy = msg.y - scene.localPlayer.y;
  const visibleRange = 600;
  if (dx * dx + dy * dy <= visibleRange * visibleRange) {
    useUIStore
      .getState()
      .showToast("⚡ Một Tinh Anh xuất hiện gần đây!", "warn");
  }
}

// Elite guaranteed drop notification — toast for now (item granting TBD).
export function onEliteDrop(_scene: FieldScene, msg: EliteDropPush) {
  useUIStore.getState().showToast(`Tinh Anh rơi: ${msg.itemId}`);
}

export function handleExpGain(_scene: FieldScene, resp: AttackResp) {
  if (resp.expGained > 0) {
    useCharacterStore.getState().applyExpGain({
      exp: resp.newExp,
      level: resp.newLevel,
      statPoints: resp.statPoints,
      skillPoints: resp.skillPoints,
    });
    useUIStore.getState().showToast(`+${resp.expGained} EXP`);
    if (resp.leveledUp) {
      useUIStore.getState().showToast(`Level Up! Lv.${resp.newLevel}`);
    }
  }
}
